import { FormattedMessage } from "react-intl";
import { Grid, useMediaQuery } from "@mui/material";
import { Add, Create } from "@mui/icons-material";

import { getEmptyTemplate } from "@dzambalaorg/mui-inputs-asset";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { emptyStateString } from "@dzambalaorg/draft-js-utils";
import { InputType, IPaginationResult } from "@dzambalaorg/types-collection";
import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import { AchievementRuleStatus, IAchievementRule, TokenType } from "@framework/types";
import { useFetchQuery } from "@dzambalaorg/react-hooks";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { Button, List } from "@dzambalaorg/ui-kit";

import { AdminDialogKeys } from "../../../../../shared";
import { achievementsRulesFilter, prepareAchievementsRulesInitialValues } from "./schemas";
import { achievementRuleValidationSchema } from "./form/validation";

const ACHIEVEMENTS_RULES_URL = "/achievements/rules";
const emptyValues = {
  title: "",
  description: emptyStateString,
  achievementStatus: AchievementRuleStatus.ACTIVE,
  contractId: InputType.awaited,
  item: getEmptyTemplate(TokenType.ERC20),
  startTimestamp: new Date().toISOString(),
  endTimestamp: new Date().toISOString(),
};

export const AchievementRules = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IAchievementRule>
  >(ACHIEVEMENTS_RULES_URL, { enableInfiniteScroll: isMd });

  const { openDialog } = useGlobalDialogFormCallbacks<IAchievementRule, IAchievementRule>();

  const achievementRuleCreate = () => {
    openDialog(AdminDialogKeys.ACHIEVEMENT_RULE_CREATE, emptyValues as IAchievementRule, {
      refetch,
      initialValues: emptyValues as IAchievementRule,
      message: "dialogs.create",
      testId: "AchievementRuleDialogForm",
      url: `${ACHIEVEMENTS_RULES_URL}`,
      filter: achievementsRulesFilter,
      validationSchema: achievementRuleValidationSchema,
    });
  };

  const achievementRuleEdit = (rule: IAchievementRule) => () => {
    openDialog(AdminDialogKeys.ACHIEVEMENT_RULE_EDIT, rule, {
      refetch,
      initialValues: rule,
      message: "dialogs.edit",
      testId: "AchievementRuleDialogForm",
      url: `${ACHIEVEMENTS_RULES_URL}/${rule.id}`,
      filter: achievementsRulesFilter,
      prepareInitialValues: prepareAchievementsRulesInitialValues,
      validationSchema: achievementRuleValidationSchema,
    });
  };

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "achievements", "achievements.rules"]} />

      <PageHeader message="pages.achievements.rules.title">
        <Button
          intent="secondary"
          startIcon={<Add />}
          onClick={achievementRuleCreate}
          data-testid="Erc721TemplateCreateButton"
        >
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(rule => (
            <StyledListItem key={rule.id}>
              <List.ItemText sx={{ width: 0.4 }}>{rule.title}</List.ItemText>
              <List.ItemText sx={{ width: 0.4 }}>{rule.contract ? rule.contract.title : "-"}</List.ItemText>
              <List.ItemText sx={{ width: 0.2 }}>{rule.eventType || "-"}</List.ItemText>
              <ListActions>
                <ListAction
                  onClick={achievementRuleEdit(rule)}
                  message="form.buttons.edit"
                  dataTestId="RuleEditButton"
                  icon={Create}
                />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={ACHIEVEMENTS_RULES_URL} refetch={refetch} />
    </Grid>
  );
};
